import { getAccessToken, refreshIfNeeded } from "@/lib/auth";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

/**
 * Sesión muerta: borramos y mandamos al usuario al login. No usamos
 * `useNavigate` (estamos fuera de React) — un `window.location.assign`
 * fuerza recarga y deja el AuthProvider en estado limpio.
 */
function redirectToLogin() {
  // Evita bucle si ya estamos en /login.
  if (window.location.pathname !== "/login") {
    const callback = encodeURIComponent(
      window.location.pathname + window.location.search
    );
    window.location.assign(`/login?callbackUrl=${callback}`);
  }
}

async function authHeader(): Promise<Record<string, string>> {
  const token = await refreshIfNeeded();
  const effective = token ?? getAccessToken();
  return effective ? { Authorization: `Bearer ${effective}` } : {};
}

/**
 * Convierte el `detail` de un error de FastAPI en un mensaje legible.
 *
 * En un 422 de validación, `detail` es un ARRAY de objetos
 * ({loc, msg, type}); pasarlo directo a `new Error()` producía toasts
 * "[object Object]". Aquí lo aplanamos a "campo: mensaje".
 */
export function formatApiErrorDetail(detail: unknown, status: number): string {
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (item && typeof item === "object" && "msg" in item) {
          const loc = Array.isArray((item as { loc?: unknown[] }).loc)
            ? (item as { loc: unknown[] }).loc.filter((l) => l !== "body").join(".")
            : "";
          const msg = String((item as { msg: unknown }).msg);
          return loc ? `${loc}: ${msg}` : msg;
        }
        return typeof item === "string" ? item : JSON.stringify(item);
      })
      .filter(Boolean);
    if (parts.length > 0) return parts.join("; ");
  }
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return `HTTP ${status}`;
}

export async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const isFormData = options.body instanceof FormData;

  const headers: Record<string, string> = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(options.headers as Record<string, string>),
    ...(await authHeader()),
  };

  const response = await fetch(`${API_URL}${url}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    redirectToLogin();
    throw new Error("Sesión expirada. Redirigiendo al login…");
  }

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: "Error desconocido" }));
    throw new Error(formatApiErrorDetail(error.detail, response.status));
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}

export async function fetchBlobWithAuth(
  url: string,
  options: RequestInit = {}
) {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
    ...(await authHeader()),
  };

  const response = await fetch(`${API_URL}${url}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    redirectToLogin();
    throw new Error("Sesión expirada. Redirigiendo al login…");
  }

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: "Error desconocido" }));
    throw new Error(formatApiErrorDetail(error.detail, response.status));
  }

  return response.blob();
}
